import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, extractErrorMessage } from '@/lib/api';
import {
  Plus, Search, UserCog, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  AlertTriangle, Truck, Upload, Trophy, Trash2, Camera, ShieldAlert, ChevronDown,
} from 'lucide-react';
import { UploadStatusRow } from '@/components/common/UploadStatusRow';
import { ExcelToolbar } from '@/components/excel/ExcelToolbar';
import { ProjectSwitcher } from '@/components/common/ProjectSwitcher';
import { DeleteConfirmDrawer } from '@/components/common/DeleteConfirmDrawer';
import { VehicleCell, CAN_ASSIGN, type PickableVehicle } from '@/components/common/VehicleCell';
import { RequestChangeDialog, diffFields, type ChangeRequestDraft } from '@/components/common/RequestChange';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import {
  useDrivers, useCreateDriver, useUpdateDriver, useExpiringDriverDocs,
  useDriverDataQuality,
} from '@/hooks/useDrivers';
import { DriverForm } from './DriverForm';
import { ComplianceBadge } from '@/components/ComplianceBadge';
import type { Driver, DriverStatus, LicenseCategory } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type StatusFilter = 'all' | DriverStatus;
type CategoryFilter = 'all' | LicenseCategory;

/** A truck the Vehicle cell can offer, and who is on it now. */
function computeDriverCompliance(d: { iqamaExpiry: string; licenseExpiry: string; medicalCertExpiry: string | null }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  const dates = [
    new Date(d.iqamaExpiry),
    new Date(d.licenseExpiry),
    ...(d.medicalCertExpiry ? [new Date(d.medicalCertExpiry)] : []),
  ];

  let hasExpired = false;
  let hasExpiringSoon = false;
  for (const exp of dates) {
    if (exp < today) hasExpired = true;
    else if (exp <= thirtyDays) hasExpiringSoon = true;
  }
  if (hasExpired) return 'expired' as const;
  if (hasExpiringSoon) return 'expiring_soon' as const;
  return 'valid' as const;
}

export default function DriversPage() {
  const [scores, setScores] = useState<Record<string, number | null>>({});
  const __navigateToPerf = useNavigate();

  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  // PR-DEL-TRIPS-DRIVERS MAY 11 — bulk delete (super_admin only).
  // PR-PERMS-CLIENTS MAY 12 — admin can delete (no-force); DeleteConfirmDrawer
  // still hides the force checkbox unless role === 'super_admin'.
  // Direct action is super_admin ONLY. An admin used to be included here, which
  // made the name untrue and let them delete outright; they now go through the
  // request queue like everyone else.
  const isSuperAdmin = user?.role === 'super_admin';
  const [crDraft, setCrDraft] = useState<ChangeRequestDraft | null>(null);
  const [crNote, setCrNote] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const toggleSelect = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  async function performBulkDelete({ force, reason }: { force: boolean; reason: string }) {
    await apiClient.post('/api/v1/drivers/bulk-delete', {
      ids: Array.from(selected),
      reason,
      force,
    });
    setSelected(new Set());
  }

  useEffect(() => {
    const tm = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      licenseCategory: categoryFilter === 'all' ? undefined : categoryFilter,
      page,
      pageSize: 25,
    }),
    [search, statusFilter, categoryFilter, page]
  );

  const { data, isLoading, isError, error, refetch } = useDrivers(params);
  const { data: expiringData } = useExpiringDriverDocs(30);
  const createMutation = useCreateDriver();
  const updateMutation = useUpdateDriver();

  // ---- assigning a truck straight from the Vehicle column
  const canAssign = CAN_ASSIGN.includes(user?.role ?? '');
  const [vehicleOptions, setVehicleOptions] = useState<PickableVehicle[]>([]);
  const [assignBusy, setAssignBusy] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<Record<string, string>>({});
  const [assignNote, setAssignNote] = useState<string | null>(null);

  // Fetched once for the whole page rather than per row.
  useEffect(() => {
    if (!canAssign) return;
    let cancelled = false;
    apiClient
      .get<{ items: PickableVehicle[] }>('/api/v1/driver-vehicle-assignments/pickable-vehicles')
      .then((r) => { if (!cancelled) setVehicleOptions(r.data.items ?? []); })
      .catch(() => { /* the cell simply offers nothing; the label still shows */ });
    return () => { cancelled = true; };
  }, [canAssign]);

  async function runAssign(driverId: string, fn: () => Promise<{ data: unknown }>) {
    setAssignBusy(driverId);
    setAssignError((p) => ({ ...p, [driverId]: '' }));
    setAssignNote(null);
    try {
      const res = await fn();
      // The server closes any assignment on either side and says so; passing
      // that on matters because reassigning silently takes a truck off someone.
      const warnings = (res.data as { warnings?: string[] })?.warnings ?? [];
      if (warnings.length) setAssignNote(warnings.join(' '));
      await refetch();
      const list = await apiClient.get<{ items: PickableVehicle[] }>(
        '/api/v1/driver-vehicle-assignments/pickable-vehicles',
      );
      setVehicleOptions(list.data.items ?? []);
    } catch (e) {
      setAssignError((p) => ({ ...p, [driverId]: extractErrorMessage(e) }));
    } finally {
      setAssignBusy(null);
    }
  }

  const assignVehicle = (driverId: string, vehicleId: string) =>
    runAssign(driverId, () => apiClient.post('/api/v1/driver-vehicle-assignments', { driverId, vehicleId }));

  const unassignVehicle = (driverId: string) =>
    runAssign(driverId, () => apiClient.post('/api/v1/driver-vehicle-assignments/unassign', { driverId }));

  const drivers = data?.items ?? [];
  useEffect(() => {
    const ids = (drivers ?? []).map((d) => d.id);
    if (ids.length === 0) { setScores({}); return; }
    let cancelled = false;
    apiClient
      .post<Record<string, number | null>>('/api/v1/driver-scoring/scores/by-driver-ids', { ids, period: 'last30' })
      .then((r) => { if (!cancelled) setScores(r.data ?? {}); })
      .catch(() => { if (!cancelled) setScores({}); });
    return () => { cancelled = true; };
  }, [drivers]);

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const expiringTotal = expiringData?.total ?? 0;

  // Records the imports left in a state nobody can vouch for. Reported, never
  // corrected here: merging two driver records decides whose licence a truck
  // is running on, which needs the paperwork, not a heuristic.
  const { data: dataQuality } = useDriverDataQuality();
  const dqCount = dataQuality?.summary.affectedRecords ?? 0;
  const [dqOpen, setDqOpen] = useState(false);
  // There is no driver detail route - a driver is edited in the drawer below,
  // from a full record this callout does not carry. So a flagged row filters
  // the list to that code instead, and the usual row click opens the usual
  // drawer. One way in, not a second one that half works.
  const showDriver = (code: string) => {
    setSearchInput(code);
    setPage(1);
    setDqOpen(false);
  };

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };

  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    // Intercepted at the handler, not the button: a control that lies about
    // what it does is worse than one that is disabled, and every route to
    // saving - form, keyboard, anything later - passes through here.
    if (!isSuperAdmin) {
      const fields = diffFields(
        editing as unknown as Record<string, unknown>,
        input as unknown as Record<string, unknown>,
      );
      setEditing(null);
      setCrDraft({
        tableName: 'drivers',
        recordId: editing.id,
        label: `${editing.code} · ${editing.fullNameEn}`,
        action: 'update',
        fields,
      });
      return;
    }
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  /** Ask for a driver to be deleted. */
  const requestDelete = (d: { id: string; code: string; fullNameEn: string }) =>
    setCrDraft({
      tableName: 'drivers',
      recordId: d.id,
      label: `${d.code} · ${d.fullNameEn}`,
      action: 'delete',
    });

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';
  const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'on_leave', 'suspended', 'terminated'];

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />
              {t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <UserCog className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('dashboard.modules.drivers')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('drivers.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {isSuperAdmin && selected.size > 0 && (
              <Button
                variant="default"
                size="lg"
                onClick={() => setDeleteOpen(true)}
                className="bg-rose-600 hover:bg-rose-700 text-white gap-1"
              >
                <Trash2 className="h-4 w-4" />
                Delete selected ({selected.size})
              </Button>
            )}
            <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
              <Plus className="h-4 w-4" />
              {t('drivers.addDriver')}
            </Button>
          </div>
        </div>

        {/* Expiring documents callout */}
        {expiringTotal > 0 && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                {t('drivers.expiringAlert', { count: expiringTotal })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('drivers.expiringHelp')}
              </p>
            </div>
          </div>
        )}

        {/* Records that cannot be trusted until someone checks them. Rose, not
            amber: an expired licence is a true fact about a real person, while
            these may not describe anyone at all. */}
        {dqCount > 0 && dataQuality && (
          <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/5 p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {dqCount} driver {dqCount === 1 ? 'record needs' : 'records need'} checking
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {dataQuality.summary.placeholders > 0 && (
                    <>
                      {dataQuality.summary.placeholders} carry a placeholder ID left by an import
                      {dataQuality.summary.placeholdersOnTruck > 0 && (
                        <> — <span className="text-rose-600 dark:text-rose-400 font-medium">
                          {dataQuality.summary.placeholdersOnTruck} of them assigned to a truck
                        </span></>
                      )}
                      {dataQuality.summary.duplicateGroups > 0 && '. '}
                    </>
                  )}
                  {dataQuality.summary.duplicateGroups > 0 && (
                    <>
                      {dataQuality.summary.duplicateGroups} name
                      {dataQuality.summary.duplicateGroups === 1 ? '' : 's'} recorded twice, across{' '}
                      {dataQuality.summary.duplicateRecords} records
                    </>
                  )}
                  . Nothing has been changed — open a driver to correct it.
                  {dataQuality.summary.sharedPhones > 0 && (
                    <>
                      {' '}Separately, {dataQuality.summary.sharedPhones} phone
                      {dataQuality.summary.sharedPhones === 1 ? ' is' : 's are'} shared by
                      different people — listed below, not counted as a fault.
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDqOpen((v) => !v)}
                className="shrink-0 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                {dqOpen ? 'Hide' : 'Show'}
                <ChevronDown className={`h-3 w-3 transition-transform ${dqOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {dqOpen && (
              <div className="mt-4 space-y-4 ps-8">
                {dataQuality.placeholderIds.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Placeholder ID numbers
                    </p>
                    <div className="space-y-1">
                      {dataQuality.placeholderIds.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => showDriver(d.code)}
                          className="w-full text-start flex items-center gap-2 text-xs hover:bg-rose-500/5 rounded px-2 py-1 -mx-2"
                        >
                          <span className="font-mono text-muted-foreground">{d.code}</span>
                          <span className="text-foreground">{d.fullNameEn}</span>
                          <span className="font-mono text-rose-600 dark:text-rose-400">
                            {d.iqamaNumber}
                          </span>
                          {d.onTruck > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400 font-medium">
                              on a truck
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {dataQuality.duplicateGroups.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Recorded twice
                      <span className="font-normal text-muted-foreground"> · one record has a placeholder ID, so it holds no identity</span>
                    </p>
                    <div className="space-y-2">
                      {dataQuality.duplicateGroups.map((g) => (
                        <div key={g.drivers.map((d) => d.id).join('-')} className="text-xs">
                          <span className="text-muted-foreground">
                            same name:
                          </span>
                          <div className="mt-0.5 space-y-0.5">
                            {g.drivers.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => showDriver(d.code)}
                                className="w-full text-start flex items-center gap-2 hover:bg-rose-500/5 rounded px-2 py-1 -mx-2"
                              >
                                <span className="font-mono text-muted-foreground">{d.code}</span>
                                <span className="text-foreground">{d.fullNameEn}</span>
                                <span className="font-mono text-muted-foreground">
                                  {d.iqamaNumber ?? '—'}
                                </span>
                                {d.onTruck > 0 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-600 dark:text-rose-400 font-medium">
                                    on a truck
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {dataQuality.sameNameDifferentPeople?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Different people who share a name
                      <span className="font-normal text-muted-foreground"> · each holds its own iqama — do not merge</span>
                    </p>
                    <div className="space-y-2">
                      {dataQuality.sameNameDifferentPeople.map((g) => (
                        <div key={g.key} className="text-xs">
                          <div className="space-y-0.5">
                            {g.drivers.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => showDriver(d.code)}
                                className="w-full text-start flex items-center gap-2 hover:bg-rose-500/5 rounded px-2 py-1 -mx-2"
                              >
                                <span className="font-mono text-muted-foreground">{d.code}</span>
                                <span className="text-foreground">{d.fullNameEn}</span>
                                <span className="font-mono text-muted-foreground">{d.iqamaNumber ?? '—'}</span>
                                {d.onTruck > 0 && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                    on a truck
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dataQuality.sharedPhones?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">
                      Shared phone numbers
                      <span className="font-normal text-muted-foreground"> · different people, one handset — not duplicates</span>
                    </p>
                    <div className="space-y-2">
                      {dataQuality.sharedPhones.map((g) => (
                        <div key={g.phone} className="text-xs">
                          <span className="font-mono text-muted-foreground">{g.phone}</span>
                          <div className="mt-0.5 space-y-0.5">
                            {g.drivers.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => showDriver(d.code)}
                                className="w-full text-start flex items-center gap-2 hover:bg-rose-500/5 rounded px-2 py-1 -mx-2"
                              >
                                <span className="font-mono text-muted-foreground">{d.code}</span>
                                <span className="text-foreground">{d.fullNameEn}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        )}

        {/* Bulk Excel. Same engine as every other entity: download the sheet,
            edit it, upload it back. The upload is validated in full and
            rejected outright if anything does not match the form. */}
        <div className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Export only. Uploading a roster from here can rewrite every driver in
                one action; that belongs on the import screen, which previews the
                change before it commits. */}
            <ExcelToolbar entity="drivers" title="drivers" exportOnly />
            {/* Which side you are looking at. The list is filtered on it
                exactly, so anything not filed under a side is only visible
                under "All sides". */}
            <ProjectSwitcher />
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-card border border-border rounded-lg p-4 mb-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('drivers.searchPlaceholder')}
                className="ps-10"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value as CategoryFilter); setPage(1); }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">{t('drivers.filter.allCategories')}</option>
              <option value="heavy">{t('drivers.licenseCategory.heavy')}</option>
              <option value="articulated">{t('drivers.licenseCategory.articulated')}</option>
              <option value="hazardous">{t('drivers.licenseCategory.hazardous')}</option>
              <option value="medium">{t('drivers.licenseCategory.medium')}</option>
              <option value="light">{t('drivers.licenseCategory.light')}</option>
              <option value="private">{t('drivers.licenseCategory.private')}</option>
              <option value="motorcycle">{t('drivers.licenseCategory.motorcycle')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                {t(`drivers.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : drivers.length === 0 ? (
            <div className="py-20 text-center">
              <UserCog className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? t('drivers.noResults')
                  : t('drivers.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' || categoryFilter !== 'all'
                  ? t('drivers.tryDifferentSearch')
                  : t('drivers.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && categoryFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('drivers.addFirstDriver')}
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Reassigning takes a truck off whoever had it. The server says
                  so; showing it is the only way that is not silent. */}
              {assignNote && (
                <div className="mx-4 mt-4 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 flex items-start justify-between gap-3">
                  <span className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-px" /> {assignNote}
                  </span>
                  <button type="button" onClick={() => setAssignNote(null)} className="underline shrink-0" aria-label="Dismiss">
                    dismiss
                  </button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      {isSuperAdmin && (
                        <th className="px-3 py-3 w-8">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-rose-600"
                            onChange={(e) => {
                              if (e.target.checked) setSelected(new Set(drivers.map((d) => d.id)));
                              else setSelected(new Set());
                            }}
                            checked={drivers.length > 0 && selected.size === drivers.length}
                          />
                        </th>
                      )}
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.code')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.name')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.iqama')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.license')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.assignedVehicle')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.compliance')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">DOCS</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> Score</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('drivers.column.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.map((d) => {
                      const compliance = computeDriverCompliance(d);
                      const assignedVehicle = d.currentVehicles?.[0];
                      return (
                        <tr
                          key={d.id}
                          onClick={() => setEditing(d)}
                          className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer"
                        >
                          {isSuperAdmin && (
                            <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-rose-600"
                                checked={selected.has(d.id)}
                                onChange={() => toggleSelect(d.id)}
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{d.code}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">{d.fullNameEn}</div>
                            {d.fullNameUr && (
                              <div className="text-xs text-muted-foreground" dir="rtl" style={{ fontFamily: '"Noto Nastaliq Urdu", "Tahoma", sans-serif' }}>
                                {d.fullNameUr}
                              </div>
                            )}
                            {d.phone && <div className="text-xs text-muted-foreground font-mono mt-0.5" dir="ltr">{d.phone}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-mono text-xs">{d.iqamaNumber}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t('drivers.exp')}: {new Date(d.iqamaExpiry).toLocaleDateString('en-CA')}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>{t(`drivers.licenseCategory.${d.licenseCategory}`)}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t('drivers.exp')}: {new Date(d.licenseExpiry).toLocaleDateString('en-CA')}
                            </div>
                          </td>
                          {/* The row opens the driver, so the picker must not
                              pass its clicks up to it. */}
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <VehicleCell
                              driverId={d.id}
                              plate={assignedVehicle?.plateEn ?? null}
                              canAssign={canAssign}
                              vehicles={vehicleOptions}
                              onAssign={assignVehicle}
                              onUnassign={unassignVehicle}
                              busy={assignBusy === d.id}
                              error={assignError[d.id] || null}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <ComplianceBadge status={compliance} />
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {d.uploadStatus && (
                              <UploadStatusRow chips={[
                                { status: d.uploadStatus.photo, icon: Camera, label: 'Driver photo',
                                  onClick: () => setEditing(d) },
                              ]} />
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <DriverScoreCell score={scores[d.id] ?? null} driverId={d.id} navigate={__navigateToPerf} />
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={
                              d.status === 'active' ? 'active' :
                              d.status === 'on_leave' ? 'gold' :
                              'inactive'
                            }>
                              {t(`drivers.status.${d.status}`)}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('drivers.showingCount', {
                    from: (page - 1) * 25 + 1,
                    to: Math.min(page * 25, total),
                    total,
                  })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('drivers.addDriver')}</SheetTitle>
            <SheetDescription>{t('drivers.addDriverDescription')}</SheetDescription>
          </SheetHeader>
          <DriverForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('drivers.create')} />
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing?.fullNameEn ?? ''}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{editing?.code} · {editing?.phone}</SheetDescription>
          </SheetHeader>
          <DriverForm
            // A super_admin may save an incomplete profile while correcting it.
            // Edit only: the create schema genuinely requires these fields.
            relaxed={user?.role === 'super_admin'} initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
          {/* Deleting here is bulk-only from the header, and that button is
              super_admin's. Without this an admin who decides a record should go
              has no way to say so - which is the whole point of the queue. */}
          {!isSuperAdmin && editing && (
            <div className="mt-6 pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => { const d = editing; setEditing(null); requestDelete(d); }}
                className="text-sm text-rose-600 dark:text-rose-400 hover:underline"
              >
                Request deletion of this driver
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* PR-DEL-TRIPS-DRIVERS MAY 11 — bulk delete drawer */}
      <RequestChangeDialog
        draft={crDraft}
        onClose={() => setCrDraft(null)}
        onSent={(m) => setCrNote(m)}
      />
      {crNote && (
        <div className="fixed bottom-4 end-4 z-50 rounded-lg border border-emerald-500/30 bg-card p-3 text-sm shadow-lg">
          {crNote}
          <button type="button" className="ms-3 underline text-xs" onClick={() => setCrNote(null)}>dismiss</button>
        </div>
      )}

      <DeleteConfirmDrawer
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        entityKind="drivers"
        label={`${selected.size} selected driver(s)`}
        forceWarning="Trips remain — driver_id set to NULL. Driver advances + salary runs purged."
        initialCounts={{ selected: selected.size }}
        performDelete={performBulkDelete}
        onDeleted={() => { refetch(); }}
      />
    </div>
  );
}

function DriverScoreCell({ score, driverId, navigate }: { score: number | null; driverId: string; navigate: (path: string) => void }) {
  const tone = score === null
    ? 'text-muted-foreground'
    : score >= 75 ? 'text-emerald-600 dark:text-emerald-400'
    : score >= 60 ? 'text-amber-600 dark:text-amber-400'
    : score >= 40 ? 'text-orange-600 dark:text-orange-400'
    :               'text-rose-600 dark:text-rose-400';
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); navigate('/dashboard/drivers/' + driverId + '/performance'); }}
      className={'font-mono text-sm hover:underline ' + tone}
      title="Open performance"
    >
      {score === null ? '—' : score.toFixed(0)}
    </button>
  );
}
